import {
  FirstLevelEntity,
  SecondLevelEntity,
  Shipment,
  ShipmentFacetsResponse,
  ShipmentFilterKeys,
  ShipmentFilters,
  ShipmentGrouping,
  ShipmentStatus,
  UNASSIGNED_RIDER_KEY,
} from "./types";
import { apiService } from "../../services/ApiService";
import { convertToQueryString } from "../../utils/util";
import { endpoints, porterEndpoints } from "../../utils/endpoints";

const addFilterParams = (
  params: URLSearchParams,
  filters?: ShipmentFilters
): void => {
  if (filters?.dispatchMode !== undefined) {
    if (filters.dispatchMode === null) {
      params.append(ShipmentFilterKeys.DISPATCH_MODE, "null");
    } else {
      params.append(ShipmentFilterKeys.DISPATCH_MODE, filters.dispatchMode);
    }
  }

  if (filters?.cptSlabLevels && filters.cptSlabLevels.length > 0) {
    filters.cptSlabLevels.forEach((level) => {
      params.append(ShipmentFilterKeys.CPT_SLAB_LEVELS, level.toString());
    });
  }

  if (filters?.cityScope) {
    params.append(ShipmentFilterKeys.CITY_SCOPE, filters.cityScope);
  }

  if (filters?.sortOrder) {
    params.append(ShipmentFilterKeys.SORT_ORDER, filters.sortOrder);
  }
};

const addStatusFilterParams = (
  params: URLSearchParams,
  statusFilter: "undelivered" | "delivered" | "cancelled"
): void => {
  if (statusFilter === "delivered") {
    params.append(ShipmentFilterKeys.INCLUDE_STATUS, ShipmentStatus.DELIVERED);
  } else if (statusFilter === "cancelled") {
    params.append(ShipmentFilterKeys.INCLUDE_STATUS, ShipmentStatus.CANCELLED);
  } else {
    params.append(ShipmentFilterKeys.EXCLUDE_STATUS, ShipmentStatus.DELIVERED);
    params.append(ShipmentFilterKeys.EXCLUDE_STATUS, ShipmentStatus.CANCELLED);
  }
};

const toApiGroupBy = (grouping: ShipmentGrouping): string => {
  if (grouping === "customer") return "customers";
  if (grouping === "supplier") return "suppliers";
  return "riders";
};

const addGroupByParam = (
  params: URLSearchParams,
  grouping: ShipmentGrouping
): void => {
  params.append(ShipmentFilterKeys.GROUP_BY, toApiGroupBy(grouping));
};

const addEntityIdParams = (
  params: URLSearchParams,
  grouping: ShipmentGrouping,
  firstLevelId?: string,
  secondLevelId?: string
): void => {
  if (grouping === "customer") {
    if (firstLevelId) {
      params.append(ShipmentFilterKeys.CUSTOMER_ID, firstLevelId);
    }
    if (secondLevelId) {
      params.append(ShipmentFilterKeys.SUPPLIER_ID, secondLevelId);
    }
    return;
  }

  if (grouping === "supplier") {
    if (firstLevelId) {
      params.append(ShipmentFilterKeys.SUPPLIER_ID, firstLevelId);
    }
    if (secondLevelId) {
      params.append(ShipmentFilterKeys.CUSTOMER_ID, secondLevelId);
    }
    return;
  }

  // Rider grouping: first level = rider phone / unassigned, second = customer
  if (firstLevelId) {
    params.append(ShipmentFilterKeys.USTAAD_MOBILE, firstLevelId);
  }
  if (secondLevelId) {
    params.append(ShipmentFilterKeys.CUSTOMER_ID, secondLevelId);
  }
};

const calculateShipmentStats = (
  totalShipments: number,
  totalUndeliveredShipments: number,
  statusFilter: "undelivered" | "delivered" | "cancelled"
) => {
  return {
    totalShipmentsCount: totalShipments,
    totalUndeliveredShipments,
    inTransitShipmentsCount: 0,
    cancelledShipmentsCount: statusFilter === "cancelled" ? totalShipments : 0,
    totalDeliveredShipments:
      statusFilter === "delivered"
        ? totalShipments
        : statusFilter === "cancelled"
        ? 0
        : totalShipments - (totalUndeliveredShipments || 0),
  };
};

const mapCustomerAddress = (addresses: any) => {
  const address = addresses?.[0];
  return {
    addressLine1: address?.addressLine1 || "",
    addressLine2: address?.addressLine2 || "",
    city: address?.city || "",
    state: address?.state || "",
    pinCode: address?.pinCode || "",
    country: address?.country || "India",
    location: {
      latitude:
        typeof address?.location === "object" && address?.location?.latitude
          ? address.location.latitude
          : 0,
      longitude:
        typeof address?.location === "object" && address?.location?.longitude
          ? address.location.longitude
          : 0,
    },
  };
};

const mapSupplierAddress = (supplierAddress: any) => {
  return {
    addressLine1: supplierAddress?.addressLine1 || "",
    addressLine2: supplierAddress?.addressLine2 || "",
    city: supplierAddress?.city || "",
    state: supplierAddress?.state || "",
    pinCode: supplierAddress?.pinCode || "",
    country: supplierAddress?.country || "",
    location: {
      latitude: supplierAddress?.location?.latitude || 0,
      longitude: supplierAddress?.location?.longitude || 0,
    },
  };
};

const EMPTY_ADDRESS = {
  addressLine1: "",
  addressLine2: "",
  city: "",
  state: "",
  pinCode: "",
  country: "",
  location: {
    latitude: 0,
    longitude: 0,
  },
};

const mapCustomerEntity = (
  item: any,
  stats: ReturnType<typeof calculateShipmentStats>
): FirstLevelEntity | SecondLevelEntity | null => {
  const customer = item.customer?.[0];
  const customerId = customer?._id || item.customerId;

  if (!customerId) {
    return null;
  }

  return {
    _id: customerId,
    name: customer?.name || "Unknown Customer",
    type: customer?.type || "Unknown",
    ...stats,
    engagementMode: customer?.engagementMode,
    address: customer
      ? mapCustomerAddress(customer.addresses)
      : { ...EMPTY_ADDRESS },
  };
};

const mapSupplierEntity = (
  item: any,
  stats: ReturnType<typeof calculateShipmentStats>
): FirstLevelEntity | SecondLevelEntity | null => {
  const supplier = item.supplier?.[0];
  const supplierId = supplier?._id || item.supplierId;

  if (!supplierId) {
    return null;
  }

  return {
    _id: supplierId,
    name: supplier?.businessName || supplier?.name || "Unknown Supplier",
    type: "supplier",
    ...stats,
    address: supplier
      ? mapSupplierAddress(supplier.address)
      : { ...EMPTY_ADDRESS },
  };
};

const mapRiderEntity = (
  item: any,
  stats: ReturnType<typeof calculateShipmentStats>
): FirstLevelEntity | null => {
  const rider = item.rider;
  const riderId =
    item._id ||
    rider?.phoneNumber ||
    UNASSIGNED_RIDER_KEY;

  if (!riderId) {
    return null;
  }

  return {
    _id: String(riderId),
    name: rider?.name || "Unassigned",
    type: "rider",
    phone: rider?.phoneNumber || "",
    ...stats,
    address: { ...EMPTY_ADDRESS },
  };
};

const groupingLabel = (grouping: ShipmentGrouping): string => {
  if (grouping === "customer") return "customers";
  if (grouping === "supplier") return "suppliers";
  return "riders";
};

export const getFirstLevelEntities = async (
  grouping: ShipmentGrouping,
  statusFilter: "undelivered" | "delivered" | "cancelled" = "undelivered",
  filters?: ShipmentFilters
): Promise<FirstLevelEntity[]> => {
  try {
    const params = new URLSearchParams();
    addGroupByParam(params, grouping);
    addStatusFilterParams(params, statusFilter);
    addFilterParams(params, filters);

    const url = `${endpoints.getShipments}?${params.toString()}`;
    const response = await apiService.axiosCruds().get(url);

    const responseData = response.data;
    const dataArray = Array.isArray(responseData)
      ? responseData
      : responseData?.shipments || [];

    const entities: FirstLevelEntity[] = dataArray
      .map((item: any): FirstLevelEntity | null => {
        const stats = calculateShipmentStats(
          item.totalShipments,
          item.totalUndeliveredShipments,
          statusFilter
        );

        if (grouping === "customer") {
          return mapCustomerEntity(item, stats) as FirstLevelEntity | null;
        }
        if (grouping === "supplier") {
          return mapSupplierEntity(item, stats) as FirstLevelEntity | null;
        }
        return mapRiderEntity(item, stats);
      })
      .filter((entity: FirstLevelEntity | null): entity is FirstLevelEntity => entity !== null);

    return entities;
  } catch (error) {
    console.error(`Error in getFirstLevelEntities (${grouping}):`, error);
    return [];
  }
};

export const getSecondLevelEntities = async (
  firstLevelId: string,
  grouping: ShipmentGrouping,
  statusFilter: "undelivered" | "delivered" | "cancelled" = "undelivered",
  filters?: ShipmentFilters
): Promise<SecondLevelEntity[]> => {
  try {
    const params = new URLSearchParams();
    const oppositeGrouping: ShipmentGrouping =
      grouping === "customer"
        ? "supplier"
        : grouping === "supplier"
        ? "customer"
        : "customer";
    addGroupByParam(params, oppositeGrouping);
    addEntityIdParams(params, grouping, firstLevelId);
    addStatusFilterParams(params, statusFilter);
    addFilterParams(params, filters);

    const url = `${endpoints.getShipments}?${params.toString()}`;
    const response = await apiService.axiosCruds().get(url);

    const responseData = response.data;
    const dataArray = Array.isArray(responseData)
      ? responseData
      : responseData?.shipments || [];

    const entities: SecondLevelEntity[] = dataArray
      .map((item: any): SecondLevelEntity | null => {
        const stats = calculateShipmentStats(
          item.totalShipments,
          item.totalUndeliveredShipments,
          statusFilter
        );

        // Customer first → suppliers; Supplier/Rider first → customers
        return grouping === "customer"
          ? (mapSupplierEntity(item, stats) as SecondLevelEntity | null)
          : (mapCustomerEntity(item, stats) as SecondLevelEntity | null);
      })
      .filter((entity: SecondLevelEntity | null): entity is SecondLevelEntity => entity !== null);

    return entities;
  } catch (error) {
    console.error(`Error in getSecondLevelEntities (${grouping}):`, error);
    return [];
  }
};

export const getShipments = async (
  firstLevelId: string,
  secondLevelId: string,
  grouping: ShipmentGrouping,
  statusFilter: "undelivered" | "delivered" | "cancelled" = "undelivered",
  filters?: ShipmentFilters
): Promise<Shipment[]> => {
  try {
    const params = new URLSearchParams();
    addStatusFilterParams(params, statusFilter);
    addEntityIdParams(params, grouping, firstLevelId, secondLevelId);
    addFilterParams(params, filters);

    const url = `${endpoints.getShipments}?${params.toString()}`;
    const response = await apiService.axiosCruds().get(url);

    const responseData = response.data;
    if (Array.isArray(responseData)) {
      return responseData as unknown as Shipment[];
    }
    return (responseData?.shipments || []) as unknown as Shipment[];
  } catch (error) {
    console.error("Error fetching shipments:", error);
    return [];
  }
};

export const getShipment = async (
  shipmentId: string,
  params?: any
): Promise<Shipment> => {
  try {
    const queryString = convertToQueryString(params);
    const response = await apiService
      .axiosCruds()
      .get(`${endpoints.getShipments}/${shipmentId}?${queryString}`);
    return response.data;
  } catch (error: any) {
    console.error("Error fetching shipment:", error);
    const errorMessage =
      error?.response?.data ||
      error?.message ||
      "Failed to fetch shipment. Please try again.";
    throw error.response?.data || new Error(errorMessage);
  }
};

export const getTracking = async (shipmentId: string) => {
  try {
    const response = await apiService
      .baseAxiosCruds()
      .get(porterEndpoints.trackShipment(shipmentId));
    return response.data;
  } catch (error) {
    console.error("Failed to update tracking for shipment:", shipmentId, error);
  }
};

export const cancelShipment = async (shipmentId: string): Promise<any> => {
  try {
    const response = await apiService
      .axiosCruds()
      .post(endpoints.cancelShipment(shipmentId));
    return response.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.errorMsg ||
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message ||
      "Failed to cancel shipment. Please try again.";
    console.error("Error cancelling shipment:", errorMessage);
    throw new Error(errorMessage);
  }
};

export const deleteShipment = async (shipmentId: string): Promise<any> => {
  try {
    const response = await apiService
      .axiosCruds()
      .delete(endpoints.deleteShipment(shipmentId));
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateShipmentService = async (
  shipmentId: string,
  payload: any
): Promise<any> => {
  try {
    const response = await apiService
      .axiosCruds()
      .patch(endpoints.updateShipment(shipmentId), payload);
    return response.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.errorMsg ||
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message ||
      "Failed to update shipment status. Please try again.";

    console.error("Error updating shipment status:", errorMessage);
    throw new Error(errorMessage);
  }
};

export const DELIVERY_OTP_REQUIRED = "DELIVERY_OTP_REQUIRED";

export class MarkConsignmentDeliveredError extends Error {
  readonly errorCode?: string;

  constructor(message: string, errorCode?: string) {
    super(message);
    this.name = "MarkConsignmentDeliveredError";
    this.errorCode = errorCode;
  }
}

export const markConsignmentDeliveredService = async (
  shipmentId: string,
  orderNo: string,
  deliveryOtp?: string
): Promise<any> => {
  try {
    const response = await apiService
      .axiosCruds()
      .post(
        `/v1/shipments/${shipmentId}/consignments/${orderNo}/mark-delivered`,
        deliveryOtp ? { deliveryOtp } : undefined
      );
    return response.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.errorMsg ||
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message ||
      "Failed to mark consignment as delivered.";
    const errorCode =
      typeof error?.response?.data?.errorCode === "string"
        ? error.response.data.errorCode
        : undefined;

    throw new MarkConsignmentDeliveredError(errorMessage, errorCode);
  }
};

export const getShipmentFacets = async (
  statusFilter: "undelivered" | "delivered" | "cancelled" = "undelivered"
): Promise<ShipmentFacetsResponse> => {
  try {
    const params = new URLSearchParams();
    addStatusFilterParams(params, statusFilter);
    params.append(
      ShipmentFilterKeys.FACETS,
      ShipmentFilterKeys.CPT_SLAB_LEVELS
    );
    params.append(ShipmentFilterKeys.FACETS, ShipmentFilterKeys.DISPATCH_MODE);
    params.append(ShipmentFilterKeys.LIMIT, "1");

    const response = await apiService
      .axiosCruds()
      .get(`${endpoints.getShipments}?${params.toString()}`);

    const responseData = response.data;

    const facets = responseData?.facets || {
      cptSlabLevels: [],
      dispatchMode: [],
    };

    return {
      shipments: responseData?.shipments || [],
      facets,
    };
  } catch (error) {
    console.error("Error fetching shipment facets:", error);
    return {
      facets: {
        cptSlabLevels: [],
        dispatchMode: [],
      },
    };
  }
};

export { groupingLabel };
